import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, UserPlus, Link2 } from "lucide-react";
import { api } from "../../lib/api";

// Inline "who is this?" picker for an unlinked-player task.
export default function LinkResolver({ t }) {
    const qc = useQueryClient();
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    useEffect(() => {
        if (q.trim().length < 2) { setResults([]); return undefined; }
        const h = setTimeout(() => api.get("/search", { params: { q: q.trim() } }).then((r) => setResults((r.data.players || []).slice(0, 6))).catch(() => {}), 250);
        return () => clearTimeout(h);
    }, [q]);
    const link = useMutation({
        mutationFn: (body) => api.post(`/admin/task-link/${encodeURIComponent(t.key)}`, body),
        onSuccess: () => {
            toast.success(t.kind === "news" ? "Linked — contract added" : "Draft pick linked");
            ["admin-tasks", "admin-tasks-count", "admin-tasks-links", "players", "contract-history"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Could not link"),
    });
    const chip = "inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-md border transition-colors disabled:opacity-50";
    const Pick = ({ p }) => (
        <button type="button" disabled={link.isPending} onClick={() => link.mutate({ player_id: p.id })} data-testid={`task-link-pick-${t.key}-${p.id}`}
            className={`${chip} border-[#22C55E]/40 bg-[#22C55E]/10 text-[#86EFAC] hover:bg-[#22C55E]/20`}>
            <Link2 className="h-3 w-3" />{p.name}{p.team_id ? ` · ${p.team_id.toUpperCase()}` : ""}{p.position ? ` · ${p.position}` : ""}
        </button>
    );
    return (
        <div className="mt-2.5 space-y-2" data-testid={`task-link-resolver-${t.key}`}>
            {!!t.candidates?.length && (
                <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[10px] font-black uppercase tracking-wider text-white/40">Same surname:</span>
                    {t.candidates.map((p) => <Pick key={p.id} p={p} />)}
                </div>
            )}
            <div className="relative max-w-sm">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/35" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search for the right player…" data-testid={`task-link-search-${t.key}`}
                    className="w-full bg-black/30 border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-[#9D4CDD]" />
            </div>
            {!!results.length && <div className="flex flex-wrap gap-1.5">{results.map((p) => <Pick key={p.id} p={p} />)}</div>}
            {t.name && !t.surname_only && (
                <button type="button" disabled={link.isPending} onClick={() => link.mutate({ create: true })} data-testid={`task-link-create-${t.key}`}
                    className={`${chip} border-white/15 text-white/70 hover:text-white`}><UserPlus className="h-3 w-3" /> New player: {t.name}</button>
            )}
        </div>
    );
}
